import dagre from '@dagrejs/dagre';
import { Position } from '@vue-flow/core';
import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import uniqueId from 'lodash/uniqueId';

import {
  IQ_TOOL_ACTION_TYPE_COMMAND,
  IQ_TOOL_ACTION_TYPE_DIALOG,
  IQ_TOOL_ACTION_TYPE_RESPONSE,
  IQ_TOOL_ACTION_TYPE_WORKFLOW,
} from '@/constants/iqtool';
import { dialogConstants } from '@/constants';
const { DIALOG_TYPES } = dialogConstants;

// Generate nodes & edges from real data
const defaultPosition = {x: 0, y: 0};
const defaultNodeHidden = false;

const defaultNodeDimensions = {
  width: 100,
  height: 50,
};
const paddingSpace = 10;
const ID_SPLIT_CHAR = '__';
const successContainerId = 'success_container';
const failureContainerId = 'failure_container';

export const LABEL_CHARS_LIMITATION = 10;
export const DIAGRAM_ZOOM_DEFAULT = 1.5;
export const DIAGRAM_ZOOM_MIN = 0.2;
export const DIAGRAM_ZOOM_MAX = 4;
export const NODE_WIDTH = 120;
// Default height for layout calculations (nodes will auto-size)
export const DEFAULT_NODE_HEIGHT_FOR_LAYOUT = 48;

/**
 * Builds an adjacency list (outbound) from the given edges.
 * @param {Array<{source: string, target: string}>} edges
 * @returns {Map<string, string[]>}
 */
export function buildAdjacencyList(edges) {
  const adjacency = new Map();
  for (const edge of edges) {
    const { source, target } = edge;
    if (!adjacency.has(source)) {
      adjacency.set(source, []);
    }
    adjacency.get(source).push(target);
  }
  return adjacency;
}

/**
 * Breadth-First Search (BFS) over a directed graph defined by nodes and edges.
 * - Starts from startNodeId (depth 0)
 * - Visits each node at most once (outbound traversal)
 * - If maxDepth is null, explores the whole reachable subgraph
 * - If maxDepth is a number, explores up to and including that depth
 *
 * @param {Array<{id: string}>} nodes
 * @param {Array<{source: string, target: string}>} edges
 * @param {string} startNodeId
 * @param {number|null} maxDepth
 * @returns {string[]} Ordered list of visited node ids
 */
export function breadthFirstSearch(nodes, edges, startNodeId, maxDepth = null) {
  if (!startNodeId || !Array.isArray(nodes) || !Array.isArray(edges)) {
    return [];
  }

  const nodeIdSet = new Set(nodes.map(n => n.id));
  if (!nodeIdSet.has(startNodeId)) {
    return [];
  }

  const adjacency = buildAdjacencyList(edges);

  const visitedNodeIds = [];
  const visitedSet = new Set();
  const queue = [];

  queue.push({ nodeId: startNodeId, depth: 0 });
  visitedSet.add(startNodeId);

  while (queue.length > 0) {
    const { nodeId, depth } = queue.shift();
    visitedNodeIds.push(nodeId);

    const isDepthLimited = typeof maxDepth === 'number';
    if (isDepthLimited && depth >= maxDepth) {
      continue;
    }

    const neighbors = adjacency.get(nodeId) || [];
    for (const neighborId of neighbors) {
      if (!nodeIdSet.has(neighborId)) {
        continue;
      }
      if (visitedSet.has(neighborId)) {
        continue;
      }
      visitedSet.add(neighborId);
      queue.push({ nodeId: neighborId, depth: depth + 1 });
    }
  }

  return visitedNodeIds;
}

/**
 * Finds a linear chain of nodes following single-child paths starting at startNodeId.
 * Stops when a node has 0 or more than 1 children, a cycle is detected, or maxDepth is reached.
 * Depth is the number of edges traversed. Depth 0 returns just the start node.
 * If maxDepth is null, the traversal is unbounded.
 *
 * @param {Array<{id: string}>} nodes
 * @param {Array<{source: string, target: string}>} edges
 * @param {string} startNodeId
 * @param {number|null} maxDepth
 * @returns {string[]} Ordered list of node ids in the chain (including start)
 */
export function findLinearChain(nodes, edges, startNodeId, maxDepth = null) {
  if (!startNodeId || !Array.isArray(nodes) || !Array.isArray(edges)) {
    return [];
  }

  const nodeIdSet = new Set(nodes.map(n => n.id));
  if (!nodeIdSet.has(startNodeId)) {
    return [];
  }

  const adjacency = buildAdjacencyList(edges);
  const chain = [startNodeId];
  const seen = new Set([startNodeId]);
  let current = startNodeId;
  let steps = 0;

  const isDepthLimited = typeof maxDepth === 'number';

  for (;;) {
    if (isDepthLimited && steps >= maxDepth) {
      break;
    }

    const neighbors = (adjacency.get(current) || []).filter(id => nodeIdSet.has(id));

    if (neighbors.length !== 1) {
      break;
    }

    const nextId = neighbors[0];

    if (seen.has(nextId)) {
      // Avoid cycles
      break;
    }

    chain.push(nextId);
    seen.add(nextId);
    current = nextId;
    steps += 1;
  }

  return chain;
}

export function useWorkflowLayout(findNode) {

  function calcNodePos(nodes, edges, direction) {
    const dagreGraph = new dagre.graphlib.Graph();

    dagreGraph.setDefaultEdgeLabel(() => ({}));

    const isHorizontal = direction === 'LR';
    dagreGraph.setGraph({
      rankdir: direction,
      ranksep: 60,
    });

    for (const node of nodes) {
      const graphNode = findNode ? findNode(node.id) : null;
      const newWidth = graphNode?.dimensions?.width || defaultNodeDimensions.width;
      const newHeight = graphNode?.dimensions?.height || defaultNodeDimensions.height;

      dagreGraph.setNode(node.id, { width: newWidth, height: newHeight });
    }

    for (const edge of edges) {
      dagreGraph.setEdge(edge.source, edge.target);
    }

    dagre.layout(dagreGraph);

    // set nodes with updated positions
    return nodes.map((node) => {
      const nodeWithPosition = dagreGraph.node(node.id);
      const divider = 2;

      const newPosition = {
        x: nodeWithPosition.x - nodeWithPosition.width / divider + paddingSpace,
        y: nodeWithPosition.y - nodeWithPosition.height / divider + paddingSpace,
      };

      return {
        ...node,
        targetPosition: isHorizontal ? Position.Left : Position.Top,
        sourcePosition: isHorizontal ? Position.Right : Position.Bottom,
        position: newPosition,
        width: nodeWithPosition.width,
        height: nodeWithPosition.height,
      };
    });
  }

  function layout(nodes, edges, direction) {

    const isHorizontal = direction === 'LR';
    const conditionalContainerGap = 100;
    let rootElementWidth = 0;

    // Step 1. Calc independent nodes
    const indNodes = cloneDeep(nodes.filter(item => !item.parentNode && item.id !== failureContainerId && item.id !== successContainerId) || []);
    const indNodesIDs = indNodes.map(item => item.id);
    const indEdges = edges.filter(item => indNodesIDs.includes(item.source) && indNodesIDs.includes(item.target)) || [];
    const indPosNodes = calcNodePos(indNodes, indEdges, direction);

    // Calculate the left and right boundaries
    let indBoundaryMin = 0;
    let indBoundaryMax = 0;
    indPosNodes.forEach(element => {
      const boundaryVal = element.position.x + element.width;
      if (boundaryVal < indBoundaryMin) {
        indBoundaryMin = boundaryVal;
      }
      if (boundaryVal > indBoundaryMax) {
        indBoundaryMax = boundaryVal;
      }
    });

    rootElementWidth = indPosNodes[0]?.width || defaultNodeDimensions.width;

    // Step 2. Calc failure container's nodes
    const failNodes = cloneDeep(nodes.filter(item => item.parentNode === failureContainerId) || []);
    const failNodesIDs = failNodes.map(item => item.id);
    const failEdges = edges.filter(item => failNodesIDs.includes(item.source) && failNodesIDs.includes(item.target)) || [];
    const failPosNodes = calcNodePos(failNodes, failEdges, direction);

    // Update failure container
    const failContainerNode = cloneDeep(nodes.find(item => item.id === failureContainerId));
    if (failContainerNode) {
      let maxY = 0;
      failPosNodes.forEach(element => {
        const boundaryY = element.position.y + element.height;
        if (boundaryY > maxY) {
          maxY = boundaryY;
        }
      });

      failContainerNode.targetPosition = isHorizontal ? Position.Left : Position.Top;
      failContainerNode.sourcePosition = isHorizontal ? Position.Right : Position.Bottom;
      failContainerNode.width = rootElementWidth + 2 * paddingSpace; // eslint-disable-line no-magic-numbers
      failContainerNode.height = maxY + paddingSpace;
      failContainerNode.position.x = indBoundaryMin - failContainerNode.width;
      if (failContainerNode.isConditionalActionContainer) {
        failContainerNode.position.y = conditionalContainerGap;
      }
      else {
        failContainerNode.position.y = 0;
      }

      failPosNodes.push(failContainerNode);
    }

    // Step 3. Calc success container's nodes
    const successNodes = cloneDeep(nodes.filter(item => item.parentNode === successContainerId) || []);
    const successNodesIDs = successNodes.map(item => item.id);
    const successEdges = edges.filter(item => successNodesIDs.includes(item.source) && successNodesIDs.includes(item.target)) || [];
    const successPosNodes = calcNodePos(successNodes, successEdges, direction);

    // Update success container
    const successContainerNode = cloneDeep(nodes.find(item => item.id === successContainerId));
    if (successContainerNode) {
      let maxY = 0;
      let maxX = 0;
      successPosNodes.forEach(element => {
        const boundaryY = element.position.y + element.height;
        const boundaryX = element.position.x + element.width;
        if (boundaryY > maxY) {
          maxY = boundaryY;
        }
        if (boundaryX > maxX) {
          maxX = boundaryX;
        }
      });

      successContainerNode.targetPosition = isHorizontal ? Position.Left : Position.Top;
      successContainerNode.sourcePosition = isHorizontal ? Position.Right : Position.Bottom;
      successContainerNode.width = maxX + paddingSpace;
      successContainerNode.height = maxY + paddingSpace;
      successContainerNode.position.x = indBoundaryMax + paddingSpace;
      if (successContainerNode.isConditionalActionContainer) {
        successContainerNode.position.y = conditionalContainerGap;
      }
      else {
        successContainerNode.position.y = 0;
      }

      successPosNodes.push(successContainerNode);
    }

    return [...indPosNodes, ...failPosNodes, ...successPosNodes];
  }

  return { layout };
}

const getNodeInfo = (actionInfo, parentNodeId = null) => {
  const newNode = {
    hidden: defaultNodeHidden,
    position: defaultPosition,
    data: {
      id: actionInfo.id, // This will be used to handle actions
      title: '',
    },
  };

  if (actionInfo.unit_type === 'action' && actionInfo.name === 'conversations.messages.create') {
    newNode.type = IQ_TOOL_ACTION_TYPE_RESPONSE;
    newNode.data.title = actionInfo?.payload?.message?.payload?.content || actionInfo.name || '';
  }
  else if (actionInfo.unit_type === IQ_TOOL_ACTION_TYPE_WORKFLOW || actionInfo.unit_type === IQ_TOOL_ACTION_TYPE_DIALOG) {
    newNode.type = actionInfo.unit_type;
    newNode.data.title = actionInfo.name || '';
  }
  else if (actionInfo.unit_type === 'action' && actionInfo.name === 'actions.run') {
    newNode.type = IQ_TOOL_ACTION_TYPE_COMMAND;
    newNode.data.title = actionInfo?.payload?.custom_name || '';
  }
  else {
    // Exception, other cases
    return null;
  }

  // Generate node id, it is just for diagram rendering
  newNode.id = getDiagramNodeIdStr(actionInfo.id, newNode.type);

  if (parentNodeId) {
    // Set child node only
    newNode.parentNode = parentNodeId;
    newNode.extent = 'parent';
    newNode.draggable = false;
  }

  return newNode;
};

const getDiagramNodeIdStr = (id, type) => {
  return `${type}${ID_SPLIT_CHAR}${id}${ID_SPLIT_CHAR}${uniqueId()}`;
};

const getConditionLabel = (conditionalStatement) => {
  const conditionList = [];
  const statements = get(conditionalStatement, 'statements', []);

  statements.forEach(condition => {
    conditionList.push(`{${condition.operand_entity}, val=${condition.value}, op=${condition.operator}}`);
  });

  return statements.length > 0 ? `if ${conditionList.join(` ${conditionalStatement.join_operator ?? ''} `)}` : '';
};

const getContainerNode = (isSuccessContainer, isConditionalActionContainer = false) => {
  return {
    id: isSuccessContainer ? successContainerId : failureContainerId,
    hidden: false,
    isConditionalActionContainer: isConditionalActionContainer,
    position: defaultPosition,
    class: `node-container-only ${isSuccessContainer ? 'success-container' : 'failure-container'}`,
  };
};

const isCommandAction = (action) => {
  return action?.unit_type === 'action' && action?.name === 'actions.run';
};

/**
 * Generate Workflow Diagram Nodes and Edges
 */
export function getWorkflowDiagramInfo(workflowInfo) {
  const nodes = [];
  const edges = [];
  const commands = [];
  const containers = [];
  const failureChildIds = [];
  const successChildIds = [];
  let parentNodeId = null;

  if (workflowInfo) {

    // 1. Root node
    const rootNode = {
      id: getDiagramNodeIdStr(workflowInfo.id, IQ_TOOL_ACTION_TYPE_WORKFLOW),
      type: IQ_TOOL_ACTION_TYPE_WORKFLOW,
      data: {
        id: workflowInfo.id,
        title: workflowInfo.name,
      },
      hidden: false,
      position: defaultPosition,
    };
    nodes.push(rootNode);

    const payloadInfo = workflowInfo.payload;
    if (payloadInfo) {

      if (payloadInfo.use_conditional_actions) {

        // 2. Conditional Actions
        if (Array.isArray(payloadInfo.conditional_states)) {

          payloadInfo.conditional_states.forEach(conditionalState => {

            if (Array.isArray(conditionalState.actions) && conditionalState.actions.length > 0) {
              const newLabel = getConditionLabel(conditionalState);
              parentNodeId = rootNode.id;

              conditionalState.actions.forEach(actionInfo => {
                const newNode = getNodeInfo(actionInfo);
                if (newNode) {
                  const newEdge = {
                    id: `${edges.length}`,
                    source: parentNodeId,
                    target: newNode.id,
                    label: parentNodeId === rootNode.id ? newLabel : '',
                    type: 'custom',
                    sourceHandle: 'source-bottom',
                    targetHandle: 'target-top',
                  };

                  nodes.push(newNode);
                  edges.push(newEdge);
                  parentNodeId = newNode.id;
                }

                if (isCommandAction(actionInfo)) {
                  commands.push(actionInfo);
                }
              });
            }

          });
        }

        // 3. Else Actions
        if (Array.isArray(payloadInfo.conditional_else_states)) {

          parentNodeId = rootNode.id;

          payloadInfo.conditional_else_states.forEach(elseCondAction => {
            const newNode = getNodeInfo(elseCondAction);
            if (newNode) {
              const newEdge = {
                id: `${edges.length}`,
                source: parentNodeId,
                target: newNode.id,
                label: 'else',
                type: 'custom',
                sourceHandle: 'source-bottom',
                targetHandle: 'target-top',
              };

              nodes.push(newNode);
              edges.push(newEdge);
              parentNodeId = newNode.id;
            }

            if (isCommandAction(elseCondAction)) {
              commands.push(elseCondAction);
            }
          });
        }
      }
      else {
        // 4. Direct actions (no conditional actions)
        if (Array.isArray(payloadInfo.states)) {
          parentNodeId = rootNode.id;
          payloadInfo.states.forEach(actionInfo => {
            const newNode = getNodeInfo(actionInfo);
            if (newNode) {
              const newEdge = {
                id: `${edges.length}`,
                source: parentNodeId,
                target: newNode.id,
                sourceHandle: 'source-bottom',
                targetHandle: 'target-top',
              };

              nodes.push(newNode);
              edges.push(newEdge);
              parentNodeId = newNode.id;
            }

            if (isCommandAction(actionInfo)) {
              commands.push(actionInfo);
            }
          });
        }
      }

      // 5. Failure Actions
      if (Array.isArray(payloadInfo.failure_actions) && payloadInfo.failure_actions.length > 0) {

        // Add child nodes of failure (without parentNode assignment)
        parentNodeId = rootNode.id;
        payloadInfo.failure_actions.forEach(actionInfo => {
          const newNode = getNodeInfo(actionInfo);
          if (newNode) {
            failureChildIds.push(newNode.id);
            const newEdge = {
              id: `${edges.length}`,
              source: parentNodeId,
              target: newNode.id,
              sourceHandle: parentNodeId === rootNode.id ? 'source-left' : 'source-bottom',
              targetHandle: parentNodeId === rootNode.id ? 'target-right' : 'target-top',
            };

            nodes.push(newNode);
            edges.push(newEdge);
            parentNodeId = newNode.id;
          }

          if (isCommandAction(actionInfo)) {
            commands.push(actionInfo);
          }
        });
      }

      // 6. Success Actions
      if (Array.isArray(payloadInfo.success_actions) && payloadInfo.success_actions.length > 0) {

        // Add child nodes of success (without parentNode assignment)
        parentNodeId = rootNode.id;
        payloadInfo.success_actions.forEach(actionInfo => {
          const newNode = getNodeInfo(actionInfo);
          if (newNode) {
            successChildIds.push(newNode.id);
            const newEdge = {
              id: `${edges.length}`,
              source: parentNodeId,
              target: newNode.id,
              sourceHandle: parentNodeId === rootNode.id ? 'source-right' : 'source-bottom',
              targetHandle: parentNodeId === rootNode.id ? 'target-left' : 'target-top',
            };

            nodes.push(newNode);
            edges.push(newEdge);
            parentNodeId = newNode.id;
          }

          if (isCommandAction(actionInfo)) {
            commands.push(actionInfo);
          }
        });
      }

    }
  }

  // Build containers array
  if (successChildIds.length > 0) {
    containers.push({ id: 'success', childIds: successChildIds });
  }
  if (failureChildIds.length > 0) {
    containers.push({ id: 'failure', childIds: failureChildIds });
  }

  // Add some data into node.data
  nodes.forEach(item => {
    if (item.data) {
      item.data.type = item.type;
    }
  });

  return {
    nodes,
    edges,
    commands,
    containers,
  };
}

/**
 * Generate Dialog Diagram Nodes and Edges
 */
export function getDialogDiagramInfo(dialogInfo) {

  const nodes = [];
  const edges = [];
  const commands = []; // Get commands for later use
  let parentNodeId = null;

  if (dialogInfo) {

    // 1. Root node
    const rootNode = {
      id: getDiagramNodeIdStr(dialogInfo.id, IQ_TOOL_ACTION_TYPE_DIALOG),
      type: IQ_TOOL_ACTION_TYPE_DIALOG,
      data: {
        id: dialogInfo.id,
        title: dialogInfo.name,
      },
      hidden: false,
      position: defaultPosition,
    };
    nodes.push(rootNode);

    const payloadInfo = dialogInfo.payload;
    if (payloadInfo?.dialog_type === DIALOG_TYPES.BUTTON) {
      // 2. Dialog Type: Button
      const buttonList = get(payloadInfo, 'list_type.response_values', []);
      buttonList.forEach(buttonInfo => {
        const newLabel = buttonInfo.value ?? '';
        const buttonActionList = get(buttonInfo, 'actions', []);
        parentNodeId = rootNode.id;

        buttonActionList.forEach(actionInfo => {
          const newNode = getNodeInfo(actionInfo);
          if (newNode) {
            const newEdge = {
              id: `${edges.length}`,
              source: parentNodeId,
              target: newNode.id,
              label: parentNodeId === rootNode.id ? newLabel : '',
              type: 'custom',
              sourceHandle: 'source-bottom',
              targetHandle: 'target-top',
            };

            nodes.push(newNode);
            edges.push(newEdge);
            parentNodeId = newNode.id;
          }

          if (isCommandAction(actionInfo)) {
            commands.push(actionInfo);
          }
        });
      });

    } else if (payloadInfo?.dialog_type === DIALOG_TYPES.TEXT || payloadInfo?.dialog_type === DIALOG_TYPES.REST) {

      // 3. Dialog Type: Text (same as REST)
      // 4. Dialog Type: REST - Entity
      const payloadData = get(payloadInfo, payloadInfo.dialog_type, {});
      let useConditionalActionFlag = false;
      if (payloadData.use_conditional_actions) {

        // 4.1 Conditional Actions in success container
        const conditionalActions = get(payloadData, 'conditional_actions', []);
        const conditionalElseActions = get(payloadData, 'conditional_else_actions', []);

        if (conditionalActions.length > 0 || conditionalElseActions.length > 0) {
          // Add success container node
          useConditionalActionFlag = true;
          nodes.push(getContainerNode(true, true));
        }

        if (conditionalActions.length > 0) {
          conditionalActions.forEach(conditionalAction => {

            if (Array.isArray(conditionalAction.actions) && conditionalAction.actions.length > 0) {
              const newLabel = getConditionLabel(conditionalAction);
              parentNodeId = rootNode.id;

              conditionalAction.actions.forEach(actionInfo => {
                const newNode = getNodeInfo(actionInfo, successContainerId);
                if (newNode) {
                  const newEdge = {
                    id: `${edges.length}`,
                    source: parentNodeId,
                    target: newNode.id,
                    label: parentNodeId === rootNode.id ? newLabel : '',
                    type: 'custom',
                    sourceHandle: parentNodeId === rootNode.id ? 'source-right' : 'source-bottom',
                    targetHandle: 'target-top',
                  };

                  nodes.push(newNode);
                  edges.push(newEdge);
                  parentNodeId = newNode.id;
                }

                if (isCommandAction(actionInfo)) {
                  commands.push(actionInfo);
                }
              });
            }

          });
        }

        // 4.2 Else Actions
        if (conditionalElseActions.length > 0) {

          parentNodeId = rootNode.id;
          conditionalElseActions.forEach(elseCondAction => {
            const newNode = getNodeInfo(elseCondAction, successContainerId);
            if (newNode) {
              const newEdge = {
                id: `${edges.length}`,
                source: parentNodeId,
                target: newNode.id,
                label: parentNodeId === rootNode.id ? 'else' : '',
                type: 'custom',
                sourceHandle: parentNodeId === rootNode.id ? 'source-right' : 'source-bottom',
                targetHandle: 'target-top',
              };

              nodes.push(newNode);
              edges.push(newEdge);
              parentNodeId = newNode.id;
            }

            if (isCommandAction(elseCondAction)) {
              commands.push(elseCondAction);
            }
          });
        }
      }
      else {
        // 4.2 Direct Success Actions
        const successActions = get(payloadData, 'success_actions', []);
        if (successActions.length > 0) {

          // Add success container node
          nodes.push(getContainerNode(true, false));

          // Add child nodes of success
          parentNodeId = rootNode.id;
          successActions.forEach(actionInfo => {
            const newNode = getNodeInfo(actionInfo, successContainerId);
            if (newNode) {
              const newEdge = {
                id: `${edges.length}`,
                source: parentNodeId,
                target: newNode.id,
                sourceHandle: parentNodeId === rootNode.id ? 'source-right' : 'source-bottom',
                targetHandle: parentNodeId === rootNode.id ? 'target-left' : 'target-top',
              };

              nodes.push(newNode);
              edges.push(newEdge);
              parentNodeId = newNode.id;
            }

            if (isCommandAction(actionInfo)) {
              commands.push(actionInfo);
            }
          });
        }
      }

      // 4.3 Direct Failure Actions
      const failureActions = get(payloadData, 'failure_actions', []);
      if (failureActions.length > 0) {
        // Add failure container node
        nodes.push(getContainerNode(false, useConditionalActionFlag));

        // Add child nodes of failure
        parentNodeId = rootNode.id;
        failureActions.forEach(actionInfo => {
          const newNode = getNodeInfo(actionInfo, failureContainerId);
          if (newNode) {
            const newEdge = {
              id: `${edges.length}`,
              source: parentNodeId,
              target: newNode.id,
              sourceHandle: parentNodeId === rootNode.id ? 'source-left' : 'source-bottom',
              targetHandle: parentNodeId === rootNode.id && !useConditionalActionFlag ? 'target-right' : 'target-top',
            };

            nodes.push(newNode);
            edges.push(newEdge);
            parentNodeId = newNode.id;
          }

          if (isCommandAction(actionInfo)) {
            commands.push(actionInfo);
          }
        });
      }

    }
  }

  // Add some data into node.data
  nodes.forEach(item => {
    if (item.data) {
      item.data.type = item.type;
    }
  });

  return {
    nodes,
    edges,
    commands,
  };
}
